import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  CONNECT_GOOGLE_ANALYTICS,
  DISCONNECT_GOOGLE_ANALYTICS,
  GET_TINYCMS_ANALYTICS,
  GET_TINYCMS_INTEGRATIONS,
  GET_TINYCMS_TODAY,
  REFRESH_SITE_ANALYTICS,
  START_GOOGLE_ANALYTICS_CONNECT,
} from '../../graphql';
import {
  GA_CONNECT_FAILED,
  GA_PROPERTIES,
  GaExchange,
  GaProperty,
  gaHomeUrl,
  takePending,
} from '../../utils/analyticsConnect';
import { showInkToast } from './InkToast';
import { updatedWhen } from './analyticsMath';
import type { AnalyticsConnection } from './todayData';
import { askInk } from './InkConfirm';

/**
 * Connecting Google Analytics (Content → Analytics, Today, Settings →
 * Analytics & code). One button: Google sign-in in a popup, then the property
 * whose web stream matches the site is connected without asking. Only when
 * none or several match does the person pick. The API stores the access and
 * the engine brings the numbers in; nothing on these screens waits on Google.
 */

const REFETCH = [GET_TINYCMS_ANALYTICS, GET_TINYCMS_TODAY, GET_TINYCMS_INTEGRATIONS];

export function useGoogleAnalyticsConnect(accountId?: string | null) {
  const [startMutation] = useMutation<{ startGoogleAnalyticsConnect: { url: string } }>(
    START_GOOGLE_ANALYTICS_CONNECT
  );
  const [connectMutation] = useMutation(CONNECT_GOOGLE_ANALYTICS, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: false,
  });
  const [offer, setOffer] = useState<GaExchange | null>(null);
  const [busy, setBusy] = useState(false);

  const connect = useCallback(
    async (exchange: GaExchange, propertyId: string) => {
      if (!exchange.ticket) return;
      setBusy(true);
      try {
        await connectMutation({
          variables: { accountId: exchange.accountId, ticket: exchange.ticket, propertyId },
        });
        setOffer(null);
        showInkToast('Google Analytics connected. Your numbers arrive in a minute or two.');
      } catch (err) {
        showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
      } finally {
        setBusy(false);
      }
    },
    [connectMutation]
  );

  const receive = useCallback(
    (result: GaExchange) => {
      if (!result || (accountId && result.accountId !== accountId)) return;
      const only = result.properties.length === 1 ? result.properties[0].propertyId : null;
      const pick = result.suggested || only;
      if (pick && result.ticket) void connect(result, pick);
      else setOffer(result);
    },
    [accountId, connect]
  );

  // The popup posts here; a copy waits in localStorage in case this window reloaded.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type === GA_PROPERTIES) {
        takePending();
        receive(data.result as GaExchange);
      } else if (data.type === GA_CONNECT_FAILED && data.message) {
        showInkToast(String(data.message));
      }
    };
    window.addEventListener('message', onMessage);
    const waiting = takePending();
    if (waiting) receive(waiting);
    return () => window.removeEventListener('message', onMessage);
  }, [receive]);

  const start = useCallback(async () => {
    if (!accountId) return;
    // Opened now, while the click still counts, so the browser doesn't block it.
    const popup = window.open('', 'google_analytics_auth', 'width=520,height=680,scrollbars=yes');
    if (!popup) {
      showInkToast('Allow pop-ups for this site to sign in with Google.');
      return;
    }
    try {
      const { data } = await startMutation({ variables: { accountId } });
      const url = data?.startGoogleAnalyticsConnect?.url;
      if (!url) throw new Error('Google sign-in isn’t available right now.');
      popup.location.href = url;
    } catch (err) {
      popup.close();
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  }, [accountId, startMutation]);

  const modal = offer ? (
    offer.properties.length ? (
      <PropertyPicker
        key={offer.ticket || 'none'}
        offer={offer}
        busy={busy}
        onCancel={() => setOffer(null)}
        onConnect={(id) => void connect(offer, id)}
      />
    ) : (
      <NoProperty email={offer.email} onClose={() => setOffer(null)} onRetry={start} />
    )
  ) : null;

  return { start, modal, connecting: busy };
}

const streamLine = (p: GaProperty) =>
  p.streams.length
    ? p.streams
        .map((s) =>
          s.uri ? s.uri.replace(/^https?:\/\//, '').replace(/\/$/, '') : s.measurementId
        )
        .join(' · ')
    : 'No website stream';

/** "Which property is this site's?" — when the sign-in can see several and none matched. */
function PropertyPicker({
  offer,
  busy,
  onCancel,
  onConnect,
}: {
  offer: GaExchange;
  busy: boolean;
  onCancel: () => void;
  onConnect: (propertyId: string) => void;
}) {
  const [picked, setPicked] = useState<string>(offer.properties[0]?.propertyId || '');
  return (
    <div className="ink-modal-back" role="dialog" aria-modal="true" aria-labelledby="gap-title">
      <div className="ink-modal chp">
        <div className="ink-modal-title" id="gap-title">
          Which property is this site?
        </div>
        <p className="ink-modal-copy">
          {offer.email ? `${offer.email} can see` : 'This Google account can see'}{' '}
          {offer.properties.length} Google Analytics properties. Pick the one your site sends its
          visits to.
        </p>
        <div className="chp-list">
          {offer.properties.map((p) => (
            <label className="chp-row" key={p.propertyId}>
              <input
                type="radio"
                name="ga-property"
                checked={picked === p.propertyId}
                disabled={busy}
                onChange={() => setPicked(p.propertyId)}
              />
              <span className="chp-name">
                <span>{p.name || `Property ${p.propertyId}`}</span>
                <span className="ink-muted">
                  {p.accountName ? `${p.accountName} · ` : ''}
                  {streamLine(p)}
                </span>
              </span>
            </label>
          ))}
        </div>
        <div className="ink-modal-actions">
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={busy}
            onClick={onCancel}
          >
            Not now
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={busy || !picked}
            onClick={() => onConnect(picked)}
          >
            {busy ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The sign-in worked but there's nothing to connect yet. */
function NoProperty({
  email,
  onClose,
  onRetry,
}: {
  email: string | null;
  onClose: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="ink-modal-back" role="dialog" aria-modal="true" aria-labelledby="gan-title">
      <div className="ink-modal chp">
        <div className="ink-modal-title" id="gan-title">
          No Google Analytics yet
        </div>
        <p className="ink-modal-copy">
          {email || 'That Google account'} doesn’t have a Google Analytics property yet. Make one
          first, then connect again.
        </p>
        <SetupSteps />
        <div className="ink-modal-actions">
          <button type="button" className="ink-btn ink-btn-quiet" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            onClick={() => {
              onClose();
              onRetry();
            }}
          >
            Connect again
          </button>
        </div>
      </div>
    </div>
  );
}

/** How to make a GA4 property, in three steps. */
export const SetupSteps: React.FC = () => (
  <ol className="an-steps">
    <li>
      Go to{' '}
      <a href="https://analytics.google.com/" target="_blank" rel="noreferrer">
        analytics.google.com
      </a>{' '}
      and sign in with your Google account. It’s free.
    </li>
    <li>
      Choose <b>Start measuring</b>, name the account and the property after your site, and pick
      your time zone.
    </li>
    <li>
      When it asks for a platform, choose <b>Web</b> and enter your site’s address. You don’t need
      to copy any code: tinycms adds it when you connect.
    </li>
  </ol>
);

/**
 * What the Analytics page shows before Google Analytics is connected: why, the
 * one button, and how to get an account. `tagOnly`: the site already sends
 * visits to GA (a G- ID is set), so the ask is shorter.
 */
export const ConnectCard: React.FC<{
  tagOnly: boolean;
  ga4Id: string | null;
  canManage: boolean;
  available: boolean;
  /** Set up in one place, Manage › Code & tracking (backlog 46b884ba). */
  connectTo: string;
}> = ({ tagOnly, ga4Id, canManage, available, connectTo }) => {
  const [showSteps, setShowSteps] = useState(false);
  return (
    <div className="an-card an-connect">
      <div className="an-connect-title">
        {tagOnly ? 'See your visitors here' : 'See who visits your site'}
      </div>
      <p className="an-connect-copy">
        {tagOnly
          ? `Your site already sends its visits to Google Analytics (${ga4Id}). Connect it, and your visitors, top articles and where they come from show here and on Today.`
          : 'Connect Google Analytics, and your visitors, top articles and where they come from show here and on Today. tinycms only reads your numbers. It never changes anything in Google Analytics.'}
      </p>
      {canManage && available ? (
        <div className="an-connect-actions">
          <Link to={connectTo} className="ink-btn ink-btn-primary">
            Connect Google Analytics
          </Link>
          {tagOnly ? null : (
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              aria-expanded={showSteps}
              onClick={() => setShowSteps((v) => !v)}
            >
              Don’t have Google Analytics yet?
            </button>
          )}
        </div>
      ) : (
        <p className="an-connect-copy ink-muted">
          {available
            ? 'Ask an admin of this site to connect Google Analytics.'
            : 'Connecting Google Analytics isn’t available yet.'}
        </p>
      )}
      {showSteps ? <SetupSteps /> : null}
    </div>
  );
};

/**
 * The line beside the range switch: where the numbers came from, when, a
 * Refresh link, and Open in GA4. While the first numbers are on their way, says so.
 */
export const AnalyticsSource: React.FC<{
  accountId: string | null | undefined;
  connection: AnalyticsConnection;
}> = ({ accountId, connection }) => {
  const [refresh, { loading }] = useMutation(REFRESH_SITE_ANALYTICS);
  const [asked, setAsked] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const ask = async () => {
    if (!accountId) return;
    try {
      await refresh({ variables: { accountId } });
      setAsked(true);
      // The page polls while a sync is due; let the link come back after a while.
      timer.current = window.setTimeout(() => setAsked(false), 60000);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };
  const syncing = connection.status === 'syncing' && !connection.syncedAt;
  return (
    <span className="an-source">
      {syncing
        ? 'Bringing in your numbers from Google Analytics…'
        : `From Google Analytics 4 · ${updatedWhen(connection.syncedAt)}`}
      {syncing || connection.status === 'reconnect' ? null : (
        <>
          {' · '}
          <button
            type="button"
            className="an-link"
            disabled={loading || asked}
            onClick={() => void ask()}
          >
            {asked ? 'Refreshing…' : 'Refresh'}
          </button>
        </>
      )}
      {' · '}
      <a
        href={gaHomeUrl(connection.propertyId)}
        target="_blank"
        rel="noreferrer"
        className="an-link"
      >
        Open in GA4 ↗
      </a>
    </span>
  );
};

/**
 * Settings → Analytics & code, under the GA4 row: whether TinyCMS reads the
 * site's numbers, as whom, and Connect / Disconnect. Disconnecting removes
 * TinyCMS's access and the numbers it brought in; the site keeps sending its
 * visits to GA (the measurement ID above stays).
 */
export const GaConnectionPanel: React.FC<{
  accountId: string | null | undefined;
  connection: AnalyticsConnection | null;
  available: boolean;
  canManage: boolean;
}> = ({ accountId, connection, available, canManage }) => {
  const ga = useGoogleAnalyticsConnect(accountId);
  const [disconnect, { loading }] = useMutation(DISCONNECT_GOOGLE_ANALYTICS, {
    refetchQueries: REFETCH,
  });
  const drop = async () => {
    if (
      !accountId ||
      !(await askInk({
        title: 'Disconnect Google Analytics?',
        body: 'tinycms stops reading your numbers and removes the ones it brought in. Your site keeps sending visits to Google Analytics.',
        confirmLabel: 'Disconnect',
        danger: true,
      }))
    )
      return;
    try {
      await disconnect({ variables: { accountId } });
      showInkToast('Google Analytics disconnected.');
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };
  return (
    <div className="an-ga-panel">
      {connection ? (
        <span>
          {connection.status === 'reconnect' ? 'Needs connecting again: ' : 'Showing numbers from '}
          <b>{connection.propertyName || `property ${connection.propertyId}`}</b>
          {connection.email ? ` · connected as ${connection.email}` : ''}
        </span>
      ) : (
        <span>
          {available
            ? 'tinycms doesn’t read your Google Analytics numbers yet.'
            : 'Reading your Google Analytics numbers isn’t available yet.'}
        </span>
      )}
      {canManage && available ? (
        <span className="an-ga-actions">
          {!connection || connection.status === 'reconnect' ? (
            <button type="button" className="an-link" onClick={ga.start}>
              {connection ? 'Connect again' : 'Connect Google Analytics'}
            </button>
          ) : null}
          {connection ? (
            <button
              type="button"
              className="an-link an-link-quiet"
              disabled={loading}
              onClick={() => void drop()}
            >
              Disconnect
            </button>
          ) : null}
        </span>
      ) : null}
      {ga.modal}
    </div>
  );
};
